import { EndpointBuilder } from "@reduxjs/toolkit/query";
import {
  ApiGeneralDataResponse,
  ApiResponse,
  ApiTags,
  CreateOrderData,
  CreateOrderGeneralInfo,
  GetAccountExtraInfoGeneralRequest,
  GetAccountExtraInfoGeneralResponse,
  GetAmountsHistoryResponse,
  GetCoinExtraExchangesDataRequest,
  GetCoinExtraExchangesDataResponse,
  GetCoinExtraGeneralDataRequest,
  GetCoinExtraGeneralDataResponse,
  GetCreateOrderGeneralInfoRequest,
  GetPairsRequest,
  GetPortfolioByAccountRequest,
  GetPortfolioDataResponse,
  GetPositionExtraGeneralDataRequest,
  GetRiskLimitOptionsRequest,
  GetRiskLimitOptionsResponse,
  GetRiskLimitRequest,
  GetRiskLimitResponse,
  LeverageOptions,
  PositionData,
  RiskLimitOption,
  SetLeverageRequest,
  SetRiskLimitRequest,
  TimeRange,
} from "@shared/types";
import { accountExtraDetailsFilters } from "@shared/slices/filters.slice";
import { getJobQuery } from "@shared/services/jobs/utils";

export const portfolioEndpoints = {
  endpoints: (build: EndpointBuilder<any, any, any>) => ({
    getPortfolioData: getJobQuery({
      build,
      endpointName: "getPortfolioDataJob",
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: () => [{ type: ApiTags.PORTFOLIO_DATA, id: "LIST" }],
    }),
    getAmountsHistory: build.query<
      GetAmountsHistoryResponse,
      { time_range: TimeRange }
    >({
      query: (args) => ({
        url: "get_amounts_history/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<GetAmountsHistoryResponse>) =>
        response?.data,
    }),
    getExchangesFromSymbol: build.query<string[], { symbol: string }>({
      query: (args) => ({
        url: "get_exchanges_from_symbol/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<string[]>) =>
        response?.data || [],
    }),
    // Coin extra details
    getCoinExtraGeneralData: build.query<
      GetCoinExtraGeneralDataResponse | undefined,
      GetCoinExtraGeneralDataRequest
    >({
      query: (args) => ({
        url: "get_coin_extra_general_data/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<GetCoinExtraGeneralDataResponse>
      ) => response?.data,
    }),
    getCoinExtraExchangesData: build.query<
      GetCoinExtraExchangesDataResponse | undefined,
      GetCoinExtraExchangesDataRequest
    >({
      query: (args) => ({
        url: "get_coin_extra_exchanges_data/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<GetCoinExtraExchangesDataResponse>
      ) => response?.data,
    }),
    // Account extra details
    getAccountExtraInfoGeneral: build.query<
      GetAccountExtraInfoGeneralResponse | undefined,
      GetAccountExtraInfoGeneralRequest
    >({
      query: (args) => ({
        url: "get_account_extra_info_general/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<GetAccountExtraInfoGeneralResponse>
      ) => response?.data,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(accountExtraDetailsFilters.actions.loadFilters({ data }));
        } catch {}
      },
    }),
    getPortfolioByAccount: build.query<
      GetPortfolioDataResponse | undefined,
      GetPortfolioByAccountRequest
    >({
      query: (args) => ({
        url: "get_portfolio_by_account/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<GetPortfolioDataResponse>) =>
        response?.data,
    }),
    getPortfolioAmount: build.query<Record<number, number>, void>({
      query: () => ({
        url: "get_portfolio_amount/",
        method: "POST",
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<Record<number, number>>>
      ) => response?.data.data,
    }),
    createOrder: build.mutation<void, CreateOrderData>({
      query: (args) => ({
        url: "create_order/",
        method: "POST",
        body: args,
      }),
    }),
    getLeverageOptions: build.query<LeverageOptions, { account_id: number }>({
      query: (args) => ({
        url: "get_leverage_options/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<LeverageOptions>>
      ) => response?.data.data,
    }),
    setLeverage: build.mutation<void, SetLeverageRequest>({
      query: (args) => ({
        url: "set_leverage/",
        method: "POST",
        body: args,
      }),
    }),
    getRiskLimitOptions: build.query<
      RiskLimitOption[],
      GetRiskLimitOptionsRequest
    >({
      query: (args) => ({
        url: "get_risk_limit_options/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: GetRiskLimitOptionsResponse) => {
        return response?.data.data.risk_limits;
      },
    }),
    getRiskLimit: build.query<number, GetRiskLimitRequest>({
      query: (args) => ({
        url: "get_risk_limit/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: GetRiskLimitResponse) => {
        return response?.data.data.risk_id;
      },
    }),
    setRiskLimit: build.mutation<void, SetRiskLimitRequest>({
      query: (args) => ({
        url: "set_risk_limit/",
        method: "POST",
        body: args,
      }),
    }),
    getPairs: build.query<string[], GetPairsRequest>({
      query: (args) => ({
        url: "get_pairs/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<string[]>>
      ) => {
        return response?.data?.data;
      },
      keepUnusedDataFor: 0,
    }),
    getCreateOrderGeneralInfo: build.query<
      CreateOrderGeneralInfo,
      GetCreateOrderGeneralInfoRequest
    >({
      query: (args) => ({
        url: "get_create_order_general_info/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<CreateOrderGeneralInfo>) => {
        return response?.data;
      },
      keepUnusedDataFor: 0,
    }),
    getPositionExtraGeneralData: build.query<
      PositionData | undefined,
      GetPositionExtraGeneralDataRequest
    >({
      query: (args) => ({
        url: "get_position_extra_general_data/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<PositionData>>
      ) => response?.data.data,
    }),
  }),
};
